import { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { resolveImage } from "@/lib/asset-map";
import { track } from "@/lib/analytics";
import { useLocale } from "@/hooks/use-locale";
import { curatedUiTranslation } from "@/lib/curated-ui-translations";

type Props = {
  title: string;
  imageUrl?: string | null;
  url: string;
  /** Optional eyebrow label (e.g. category name) */
  eyebrow?: string | null;
};

/**
 * "Share to Story" - generates a 1080×1920 story-format image client-side
 * (article cover + title + smartasaurus.com URL) and pushes it through the
 * native share sheet so the user can tap → Instagram → "Stories" / TikTok /
 * Facebook / Threads with the image pre-loaded. No native SDK required.
 *
 * Desktop / unsupported browsers: downloads the image instead.
 */
export function ShareToStory({ title, imageUrl, url, eyebrow }: Props) {
  const [lang] = useLocale();
  const shareLabel = curatedUiTranslation(lang, "Share to Story") ?? "Share to Story";
  const [loading, setLoading] = useState(false);

  const buildImage = async (): Promise<Blob> => {
    const W = 1080;
    const H = 1920;
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d")!;

    // Background fallback
    ctx.fillStyle = "#0b0b0f";
    ctx.fillRect(0, 0, W, H);

    // Draw cover image (cover-fit, top portion)
    const resolved = resolveImage(imageUrl ?? "");
    if (resolved) {
      try {
        const img = await loadImage(resolved);
        const targetH = Math.round(H * 0.72);
        const ratio = Math.max(W / img.width, targetH / img.height);
        const dw = img.width * ratio;
        const dh = img.height * ratio;
        ctx.drawImage(img, (W - dw) / 2, (targetH - dh) / 2, dw, dh);

        // Dark gradient overlay bottom for readability
        const grad = ctx.createLinearGradient(0, targetH - 400, 0, H);
        grad.addColorStop(0, "rgba(11,11,15,0)");
        grad.addColorStop(0.5, "rgba(11,11,15,0.85)");
        grad.addColorStop(1, "rgba(11,11,15,1)");
        ctx.fillStyle = grad;
        ctx.fillRect(0, targetH - 400, W, H - (targetH - 400));
      } catch { /* fall through with solid bg */ }
    }

    // Eyebrow
    ctx.fillStyle = "#ff5b3a";
    ctx.font = "700 32px system-ui, -apple-system, sans-serif";
    ctx.textAlign = "left";
    const eb = (eyebrow ?? "SMARTASAURUS").toUpperCase();
    ctx.fillText(eb, 80, 1380);

    // Title (wrapped, big, bold)
    ctx.fillStyle = "#ffffff";
    ctx.font = "800 78px system-ui, -apple-system, sans-serif";
    const lines = wrapText(ctx, title, W - 160, 6);
    let y = 1470;
    for (const line of lines) {
      ctx.fillText(line, 80, y);
      y += 92;
    }

    // CTA pill
    const ctaY = 1720;
    ctx.fillStyle = "#ff5b3a";
    roundRect(ctx, 80, ctaY, W - 160, 110, 55);
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.font = "800 40px system-ui, -apple-system, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("READ FULL STORY", W / 2, ctaY + 56);
    ctx.font = "600 30px system-ui, -apple-system, sans-serif";
    ctx.fillText("smartasaurus.com", W / 2, ctaY + 92);

    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("blob fail"))), "image/jpeg", 0.92),
    );
  };

  const onClick = async () => {
    setLoading(true);
    track("Share To Story Click", {});
    try {
      const blob = await buildImage();
      const file = new File([blob], "smartasaurus-story.jpg", { type: "image/jpeg" });

      const nav = navigator as Navigator & {
        share?: (d: ShareData) => Promise<void>;
        canShare?: (d: ShareData) => boolean;
      };

      // Always copy the link first so pasting in Instagram's link sticker is one tap.
      try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }

      if (nav.canShare?.({ files: [file] }) && nav.share) {
        try {
          await nav.share({ files: [file], title, text: title, url });
          toast.success("Link copied - paste into your story sticker.");
          track("Share To Story Success", { method: "native" });
          return;
        } catch (err) {
          // user cancelled - silent
          if ((err as Error).name === "AbortError") return;
        }
      }

      // Fallback: download the image (link already copied above)
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = "smartasaurus-story.jpg";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
      toast.success("Story image saved. Link copied - paste into your story.");
      track("Share To Story Success", { method: "download" });
    } catch (err) {
      console.error(err);
      toast.error("Couldn't build the story image");
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-primary transition hover:bg-primary/20 disabled:opacity-60"
      aria-label={shareLabel}
    >
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
      <span>{shareLabel}</span>
    </button>
  );
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxLines: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
      if (lines.length === maxLines - 1) break;
    } else {
      line = test;
    }
  }
  if (line && lines.length < maxLines) lines.push(line);
  // Truncate last line if needed
  if (lines.length === maxLines) {
    let last = lines[maxLines - 1];
    while (ctx.measureText(last + "…").width > maxWidth && last.length > 0) last = last.slice(0, -1);
    lines[maxLines - 1] = last + "…";
  }
  return lines;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
